import { useId, useState } from "react";
import { FieldError, FieldRoot } from "@design-system/Field/FieldParts";
import { OTPField } from "@design-system/OTPField/OTPField";
import type { OTPFieldProps } from "@design-system/OTPField/OTPField.types";
import { VStack } from "@design-system/VStack/VStack";
import { VariantGrid, VariantItem } from "./playgroundControls";

export const codeFillStates = ["empty", "partial", "complete"] as const;

export type CodeFillState = (typeof codeFillStates)[number];

export const codeFillValues: Record<CodeFillState, string> = {
    empty: "",
    partial: "123",
    complete: "123456",
};

type LiveOTPFieldProps = Omit<OTPFieldProps, "value" | "onValueChange"> & {
    initialValue: string;
};

/** OTPField holding its own value, so every matrix cell stays editable. */
const LiveOTPField = ({ initialValue, ...props }: LiveOTPFieldProps) => {
    const [value, setValue] = useState(initialValue);

    return (
        <OTPField
            aria-label="Verification code"
            {...props}
            value={value}
            onValueChange={setValue}
        />
    );
};

/** The flow's wrong-code composition: Field context marks every slot invalid. */
const FieldInvalidOTPField = () => {
    const errorId = useId();

    return (
        <VStack align="center">
            <FieldRoot invalid>
                <LiveOTPField
                    initialValue={codeFillValues.complete}
                    aria-describedby={errorId}
                />
                <FieldError id={errorId} match>
                    That code is invalid. Try again.
                </FieldError>
            </FieldRoot>
        </VStack>
    );
};

/**
 * Every fill and state, one stage each. Hover and focus are forced on the
 * active slot (the first empty one); invalid and disabled are props.
 */
export const OTPFieldMatrix = () => (
    <>
        <VariantGrid title="Fill" columns="wide">
            <VariantItem label="empty">
                <LiveOTPField initialValue={codeFillValues.empty} />
            </VariantItem>
            <VariantItem label="partial">
                <LiveOTPField initialValue={codeFillValues.partial} />
            </VariantItem>
            <VariantItem label="complete">
                <LiveOTPField initialValue={codeFillValues.complete} />
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="States" columns="wide">
            <VariantItem label='data-forced-state="hover"'>
                <LiveOTPField
                    initialValue={codeFillValues.partial}
                    data-forced-state="hover"
                />
            </VariantItem>
            <VariantItem label='data-forced-state="focus"'>
                <LiveOTPField
                    initialValue={codeFillValues.partial}
                    data-forced-state="focus"
                />
            </VariantItem>
            <VariantItem label="aria-invalid">
                <LiveOTPField
                    initialValue={codeFillValues.complete}
                    aria-invalid
                />
            </VariantItem>
            <VariantItem label="FieldRoot invalid · FieldError">
                <FieldInvalidOTPField />
            </VariantItem>
            <VariantItem label='aria-invalid · data-forced-state="hover"'>
                <LiveOTPField
                    initialValue={codeFillValues.partial}
                    aria-invalid
                    data-forced-state="hover"
                />
            </VariantItem>
            <VariantItem label="disabled">
                <LiveOTPField initialValue={codeFillValues.partial} disabled />
            </VariantItem>
            <VariantItem label='readOnly · data-forced-state="focus"'>
                <LiveOTPField
                    initialValue={codeFillValues.partial}
                    readOnly
                    data-forced-state="focus"
                />
            </VariantItem>
        </VariantGrid>
    </>
);
